import React from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  Box, Container, Typography, useMediaQuery, useTheme, Grid, Stack, Skeleton,
} from "@mui/material";
import HeroSection from "../../components/common/HeroSection";
import AboutPage from "../../components/common/AboutPage";
import CountryQuickHighlights from "../../components/common/CountryQuickHighlights";
import WhyStudyGermany from "../../components/about-germany/WhyStudyGermany";
import { getAboutPage } from "../../services/AboutPageService";
import WhatsApp from "../../assets/whatsapp.png";


const AboutGermany = () => {
  const { country_id, country_name } = useSelector((state) => state.auth || {});
  
  const { data: countryData, isLoading: loading } = useQuery({
    queryKey: ["aboutPage", country_id],
    queryFn: async () => {
      if (!country_id) return null;
      const res = await getAboutPage(country_id);
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      }
      return null;
    },
    enabled: !!country_id,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getResponsiveHtml = (html) => {
    if (!html) return "";
    if (!isMobile) return html.replace(/<br\s*\/?>/gi, "").replace(/\u00A0/gi, " ").replace(/&nbsp;/gi, "").replace(/\[empty_line\]/gi, "<br />");
    return html
      .replace(/font-size:\s*\d+px/gi, "font-size: 18px")
      .replace(/font-weight:\s*900/gi, "font-weight: 700")
      .replace(/\u00A0/gi, " ")
      .replace(/&nbsp;/gi, "")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/\[empty_line\]/gi, "<br />");
  };

  if (loading) {
    return (
      <Container
        sx={{
          py: { xs: 3, sm: 4, md: 6 },
          px: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="text" height={60} width="80%" />
            <Skeleton variant="text" height={30} width="90%" />
            <Skeleton variant="text" height={30} width="85%" />
            <Stack direction="row" spacing={2} mt={3}>
              <Skeleton variant="rounded" width={140} height={45} />
              <Skeleton variant="rounded" width={140} height={45} />
            </Stack>
            <Stack spacing={2} mt={3}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="75%" />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!countryData)
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <p>No data found for {country_name || "selected country"}.</p>
      </Box>
    );

  const heroTitleContent = countryData.hero_section_title || "";
  const heroImageType = countryData.hero_section_image_type || "image";
  const heroSubtitleContent = countryData.hero_section_desc || "";
  const heroImage = countryData.hero_section_image || "";
  const buttonDesc = countryData.button_desc || "";

  const stats =
    countryData.quick_states_highlights?.map((item) => ({
      title: item.key,
      subtitle: item.value,
    })) || [];

  const whyStudy =
    countryData.why_study_in?.map((item) => ({
      key: item.key,
      value: item.value,
    })) || [];

  const aboutContent = {
    desc: countryData.about_desc,
    image: countryData.about_image,
    imageType: countryData.about_image_type,
    why_study_in_title: countryData.why_study_in_title,
    about_title: countryData.about_title
  };

  const shortdesc = countryData.short_desc;

  return (
    <>
      <HeroSection
        titleContent={heroTitleContent}
        subtitleContent={heroSubtitleContent}
        buttons={[
          { label: "Book Free Guidance", link: "#bookfreeguidance" },
          {
            label: "Whatsapp",
            link: "whatsapp",
            icon: <img src={WhatsApp} alt="whatsapp" style={{ width: 24, height: 24 }} />,
          },
        ]}
        buttonsDesc={buttonDesc}
        heroImage={heroImage}
        heroImageType={heroImageType}
      />
      <CountryQuickHighlights stats={stats} />
      <AboutPage
        title={aboutContent.about_title}
        desc={aboutContent.desc}
        image={aboutContent.image}
        image_type={aboutContent.imageType}
      />
      <WhyStudyGermany title={aboutContent.why_study_in_title} data={whyStudy} />
      {shortdesc && (
        <Container>
          <Box sx={{ mt: { xs: 2, md: 0 }, width: "100%", zIndex: 2, display: "flex" }}>
            <Typography dangerouslySetInnerHTML={{ __html: getResponsiveHtml(shortdesc) }} />
          </Box>
        </Container>
      )}
    </>
  );
};

export default AboutGermany;
